import React, { useState, useMemo, useRef } from 'react';
import { PlusCircle, Trash2 } from 'lucide-react';
import axios from 'axios';
import JoditEditor from 'jodit-react';
import { Base_Url } from '../config';
import { FaPlus } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const VersionDownloadForm = () => {
  const [formData, setFormData] = useState({
    softwareName: '',
    versions: [
      {
        versionName: '',
        platforms: [{ name: '', disks: [{ diskNumber: 1, diskSize: '', downloadLink: '' }] }],
      },
    ],
    setupGuide: '',
  });

  const navigate = useNavigate();

  const editor = useRef(null);
  const [content, setContent] = useState('');

  const editorConfig = useMemo(() => ({
    readonly: false,
    minHeight: 400,
    editHTMLDocumentMode: true,
    defaultMode: 1,
    enter: 'P',
    useSearch: true,
    buttons: [
      'source', '|', 'bold', 'italic', 'underline', 'strikethrough', 'eraser',
      '|', 'ul', 'ol', 'indent', 'outdent', '|', 'font', 'fontsize',
      'paragraph', 'lineHeight', 'brush', '|', 'superscript', 'subscript',
      'classSpan', '|', 'file', 'image', 'video', '|', 'spellcheck',
      'speechRecognize', '|', 'cut', 'copy', 'paste', 'selectall',
      'copyformat', '|', 'hr', 'table', 'link', 'symbols', '|',
      'ai-commands', 'ai-assistant', '|', 'left', '|', 'undo', 'redo',
      '|', 'find', 'fullsize', 'preview', 'print', 'about'
    ],
    buttonsXS: [
      'ul',
      'ol',
      'paragraph',
      'source'
    ],
    controls: {
      ul: {
        list: {
          default: 'Default',
          circle: 'Circle',
          disc: 'Disc',
          square: 'Square',
        },
      },
    },
    list: {
      indent: true,
      startIndent: 20,
    },
    toolbarAdaptive: false,
    toolbarSticky: true,
    addNewLineOnDBLClick: true,
    toolbarButtonSize: 'middle',
    style: {
      ul: {
        'list-style-type': 'disc',
        'margin-left': '20px',
        'padding-left': '20px'
      },
      li: {
        'list-style': 'inherit',
        'margin-left': '10px'
      }
    },
  }), []);

  const handleSubmit = async (e) => {
    e.preventDefault();
  
    const preparedData = {
      softwareName: formData.softwareName,
      versions: formData.versions.map(version => ({
        versionName: version.versionName,
        platforms: version.platforms.map(platform => ({
          name: platform.name,
          disks: platform.disks.filter(disk => disk.downloadLink) // Filter out empty disks
        }))
      })),
      setupGuide: content, // Content from the Jodit editor
    };
  
    try {
      // First, create the software
      const softwareResponse = await axios.post(`${Base_Url}/software`, {
        name: preparedData.softwareName
      });
      const softwareId = softwareResponse.data.id;
  
      // Then, create the versions
      for (const version of preparedData.versions) {
        const versionResponse = await axios.post(`${Base_Url}/version`, {
          software_id: softwareId,
          version_name: version.versionName
        });
        const versionId = versionResponse.data.id;
  
        // Then, create the platforms and disks
        for (const platform of version.platforms) {
          const platformResponse = await axios.post(`${Base_Url}/platform`, {
            version_id: versionId,
            platform_name: platform.name
          });
          const platformId = platformResponse.data.id;
  
          for (const disk of platform.disks) {
            await axios.post(`${Base_Url}/disk`, {
              platform_id: platformId,
              disk_number: disk.diskNumber,
              disk_size: disk.diskSize,
              download_link: disk.downloadLink
            });
          }
        }
      }
  
      // Now, send the setup guide content to /scisoftpages
      const sciSoftPageResponse = await axios.post(`${Base_Url}/scisoftpages`, {
        software_id: softwareId,
        content: preparedData.setupGuide // Content from the Jodit editor
      });
  
      // console.log('Data successfully submitted');
      // Reset form after successful submission
      setFormData({
        softwareName: '',
        versions: [
          {
            versionName: '',
            platforms: [{ name: '', disks: [{ diskNumber: 1, diskSize: '', downloadLink: '' }] }],
          },
        ],
      });
      navigate('/services-software-scientific');
      setContent(''); // Reset editor content
    } catch (error) {
      console.error('There was an error submitting the form:', error);
    }
  };

  const addVersion = () => {
    setFormData(prev => ({
      ...prev,
      versions: [...prev.versions, { versionName: '', platforms: [{ name: '', disks: [{ diskNumber: 1, diskSize: '', downloadLink: '' }] }] }]
    }));
  };

  const removeVersion = (index) => {
    setFormData(prev => ({
      ...prev,
      versions: prev.versions.filter((_, i) => i !== index)
    }));
  };

  const addPlatform = (versionIndex) => {
    setFormData(prev => ({
      ...prev,
      versions: prev.versions.map((version, i) =>
        i === versionIndex
          ? {
              ...version,
              platforms: [...version.platforms, { name: '', disks: [{ diskNumber: 1, diskSize: '', downloadLink: '' }] }]
            }
          : version
      )
    }));
  };

  const removePlatform = (versionIndex, platformIndex) => {
    setFormData(prev => ({
      ...prev,
      versions: prev.versions.map((version, i) =>
        i === versionIndex
          ? {
              ...version,
              platforms: version.platforms.filter((_, j) => j !== platformIndex)
            }
          : version
      )
    }));
  };

  const addDisk = (versionIndex, platformIndex) => {
    setFormData(prev => ({
      ...prev,
      versions: prev.versions.map((version, i) =>
        i === versionIndex
          ? {
              ...version,
              platforms: version.platforms.map((platform, j) =>
                j === platformIndex
                  ? {
                      ...platform,
                      disks: [...platform.disks, { diskNumber: platform.disks.length + 1, diskSize: '', downloadLink: '' }]
                    }
                  : platform
              )
            }
          : version
      )
    }));
  };

  const removeDisk = (versionIndex, platformIndex, diskIndex) => {
    setFormData(prev => ({
      ...prev,
      versions: prev.versions.map((version, i) =>
        i === versionIndex
          ? {
              ...version,
              platforms: version.platforms.map((platform, j) =>
                j === platformIndex
                  ? {
                      ...platform,
                      disks: platform.disks.filter((_, k) => k !== diskIndex)
                    }
                  : platform
              )
            }
          : version
      )
    }));
  };

  const updateVersionName = (index, value) => {
    setFormData(prev => ({
      ...prev,
      versions: prev.versions.map((version, i) =>
        i === index ? { ...version, versionName: value } : version
      )
    }));
  };

  const updatePlatformName = (versionIndex, platformIndex, value) => {
    setFormData(prev => ({
      ...prev,
      versions: prev.versions.map((version, i) =>
        i === versionIndex
          ? {
              ...version,
              platforms: version.platforms.map((platform, j) =>
                j === platformIndex ? { ...platform, name: value } : platform
              )
            }
          : version
      )
    }));
  };

  const updateDisk = (versionIndex, platformIndex, diskIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      versions: prev.versions.map((version, i) =>
        i === versionIndex
          ? {
              ...version,
              platforms: version.platforms.map((platform, j) =>
                j === platformIndex
                  ? {
                      ...platform,
                      disks: platform.disks.map((disk, k) =>
                        k === diskIndex ? { ...disk, [field]: value } : disk
                      )
                    }
                  : platform
              )
            }
          : version
      )
    }));
  };

  return (
    <div className="card mx-auto" style={{ maxWidth: '800px' }}>
      <div className="card-header">
        <h5 className="card-title mb-0">Add Version Download</h5>
      </div>
      <div className="card-body">
        <form onSubmit={handleSubmit} className="needs-validation">
          <div className="mb-3">
            <label className="form-label">Software Name</label>
            <input
              type="text"
              className="form-control"
              value={formData.softwareName}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                softwareName: e.target.value
              }))}
              placeholder="e.g. Software Name"
              required
            />
          </div>

          {formData.versions.map((version, versionIndex) => (
            <div key={versionIndex} className="card mb-3">
              <div className="card-body">
                <div className="input-group mb-3">
                  <input
                    type="text"
                    className="form-control"
                    value={version.versionName}
                    onChange={(e) => updateVersionName(versionIndex, e.target.value)}
                    placeholder="e.g. Version 2024R2"
                    required
                  />
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    onClick={() => removeVersion(versionIndex)}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>

                <label className="form-label">Platforms</label>
                {version.platforms.map((platform, platformIndex) => (
                  <div key={platformIndex} className="card mb-3">
                    <div className="card-body">
                      <div className="input-group mb-3">
                        <input
                          type="text"
                          className="form-control"
                          value={platform.name}
                          onChange={(e) => updatePlatformName(versionIndex, platformIndex, e.target.value)}
                          placeholder="e.g. Windows, Linux ,Mac"
                          required
                        />
                        <button
                          type="button"
                          className="btn btn-outline-danger"
                          onClick={() => removePlatform(versionIndex, platformIndex)}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>

                      <label className="form-label">Disks</label>
                      {platform.disks.map((disk, diskIndex) => (
                        <div key={diskIndex} className="input-group mb-2">
                          <input
                            type="text"
                            className="form-control"
                            value={disk.diskSize}
                            onChange={(e) => updateDisk(versionIndex, platformIndex, diskIndex, 'diskSize', e.target.value)}
                            placeholder="Name"
                            required
                          />
                          <input
                            type="url"
                            className="form-control"
                            value={disk.downloadLink}
                            onChange={(e) => updateDisk(versionIndex, platformIndex, diskIndex, 'downloadLink', e.target.value)}
                            placeholder="Download Link"
                            required
                          />
                          <button
                            type="button"
                            className="btn btn-outline-danger"
                            onClick={() => removeDisk(versionIndex, platformIndex, diskIndex)}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      ))}

                      <button
                        type="button"
                        className="btn btn-outline-primary w-100"
                        onClick={() => addDisk(versionIndex, platformIndex)}
                      >
                        <PlusCircle size={16} className="me-2" />
                        Add Disk
                      </button>
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  className="btn btn-outline-primary w-100"
                  onClick={() => addPlatform(versionIndex)}
                >
                  <PlusCircle size={16} className="me-2" />
                  Add Platform
                </button>
              </div>
            </div>
          ))}

          <button
            type="button"
            className="btn btn-outline-primary w-100"
            onClick={addVersion}
          >
            <PlusCircle size={16} className="me-2" />
            Add Version
          </button>

          <div className="form-group">
            <label>Setup Guide</label>
            <div className="editor-container">
              <JoditEditor
                ref={editor}
                value={content}
                config={editorConfig}
                tabIndex={1}
                onBlur={(newContent) => setContent(newContent)}
                onChange={() => {}}
              />
            </div>
          </div>

          <button type="submit" className="btn btn-primary w-100">
            Save Version
          </button>
        </form>
      </div>
    </div>
  );
};

export default VersionDownloadForm;